// frontend/src/features/ups/BannedDriverEditModal.js
// This modal displays a banned driver’s details and, if the user is a UPS admin, lets them edit and save the ban reason via the UPS service.

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsUpsAdmin } from '../../features/auth/authSlice';
import { updateBan } from '../../services/upsDriverService';

const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';
const labelClass = 'mb-1 block text-sm font-medium text-app-muted';
const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

// Modal for viewing and optionally editing ban details
export default function BannedDriverEditModal({ ban, onClose, onUpdate }) {
  const isAdmin = useSelector(selectIsUpsAdmin);
  const [banReason, setBanReason] = useState(ban?.ban_reason || '');
  const [error, setError] = useState(null);

  const safeDate = v => (v ? new Date(v).toLocaleString() : '—');

  // Submit updated ban reason (admin only)
  const handleSubmit = async e => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      const reason = banReason.trim();
      if (!reason) return;

      await updateBan(ban?.ban_id, reason);

      // Phase 6 Option A:
      // Parent now treats onUpdate as "refresh the list", not "here is an updated DTO".
      onUpdate?.();

      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error updating ban record');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
          <h2 className="text-xl font-semibold text-app-text">Ban Details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close ban details modal"
            className="rounded-xl px-3 py-2 text-xl text-app-muted transition hover:bg-app-surface hover:text-app-text"
          >
            &times;
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-app-border bg-app-surface p-4 text-sm sm:grid-cols-2">
            <p><strong>Driver:</strong> {ban?.name || '—'}</p>
            <p><strong>Vehicle:</strong> {ban?.vehicle_registration_number || '—'}</p>
            <p><strong>Phone:</strong> {ban?.phone || '—'}</p>
            <p><strong>Email:</strong> {ban?.email || '—'}</p>
            <p><strong>Agency:</strong> {ban?.workforce_source === 'COMPANY' ? 'Internal' : (ban?.agency_name || '—')}</p>
            <p><strong>Submitted:</strong> {safeDate(ban?.submission_date)}</p>
            <p className="sm:col-span-2"><strong>Banned At:</strong> {safeDate(ban?.banned_at)}</p>
          </div>

          <form id="ban-edit-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="ban-reason" className={labelClass}>
                Ban Reason
              </label>
              <textarea
                id="ban-reason"
                rows={3}
                required
                value={banReason}
                onChange={e => setBanReason(e.target.value)}
                disabled={!isAdmin}
                className={inputClass}
              />
            </div>
          </form>
        </div>

        <div
          className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onClose}
            className={secondaryButtonClass}
          >
            Close
          </button>
          <button
            type="submit"
            form="ban-edit-form"
            disabled={!isAdmin}
            title={isAdmin ? 'Update ban reason' : 'Admins only'}
            className={primaryButtonClass}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
}
